import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel, TextField, Paper, Alert } from '@mui/material';
import { AccountBalance, Scale, Handshake, Gavel, Assignment, HomeWork, AccountBox, People, Info, Gavel as GavelIcon, FileCopy, Handshake as HandshakeIcon, Business, Receipt } from '@mui/icons-material';
import { SkillEntry } from '../../../types/profileForm.types';

interface TracksAndServicesSectionProps {
  onTrackSelectionChange: (hasProfessional: boolean, hasODR: boolean, selectedRolesArray: string[]) => void;
  selectedRolesArray?: string[];
}

const TracksAndServicesSection: React.FC<TracksAndServicesSectionProps> = ({
  onTrackSelectionChange,
  selectedRolesArray = []
}) => {
  const [initialRoles, setInitialRoles] = useState<string[]>([]);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    if (!initialCheckDone && selectedRolesArray.length > 0) {
      setInitialRoles([...selectedRolesArray]);
      setInitialCheckDone(true);
    }
    const timer = setTimeout(() => setInitialCheckDone(true), 1000);
    return () => clearTimeout(timer);
  }, [selectedRolesArray, initialCheckDone]);

  const toggleRole = (roleId: string) => {
    // If the role is verified/initial, it cannot be unchecked
    if (initialRoles.includes(roleId)) return;

    const newRoles = selectedRolesArray.includes(roleId)
      ? selectedRolesArray.filter(t => t !== roleId)
      : [...selectedRolesArray, roleId];

    const hasODR = newRoles.includes('mediator') || newRoles.includes('arbitrator') || newRoles.includes('negotiator');
    const hasProf = newRoles.includes('lawyer') || newRoles.includes('ca') || newRoles.includes('cs') || newRoles.includes('other') || hasODR;
    onTrackSelectionChange(hasProf, hasODR, newRoles);
  };

  let showLegal = false, showCorp = false, showODR = false, showTax = false;
  selectedRolesArray.forEach(track => {
    if (track === 'lawyer') { showLegal = true; showCorp = true; showTax = true; }
    if (track === 'ca' || track === 'cs') { showCorp = true; showTax = true; }
    if (track === 'other') { showLegal = true; showCorp = true; showTax = true; showODR = true; }
    if (['mediator', 'arbitrator', 'negotiator'].includes(track)) { showODR = true; }
  });

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '8px' }}>
        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, mb: 1 }}>
          1. Select Your Professional Roles
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select all that apply. Your selection populates the relevant services and legal agreements below.
        </Typography>

        {initialRoles.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Verified roles are locked and cannot be removed. You may select additional roles if you wish to expand your services.
          </Alert>
        )}
        {initialRoles.length === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Note:</strong> Once you submit your profile with these roles, they will be locked for verification and cannot be removed later.
          </Alert>
        )}

        <Grid container spacing={2}>
          {[
            { id: 'lawyer', label: 'Advocate (Lawyer)', icon: <Scale /> },
            { id: 'ca', label: 'Chartered Accountant', icon: <AccountBalance /> },
            { id: 'cs', label: 'Company Secretary', icon: <HomeWork /> },
            { id: 'other', label: 'Other Professional', icon: <AccountBox /> },
            { id: 'mediator', label: 'Mediator', icon: <Handshake /> },
            { id: 'arbitrator', label: 'Arbitrator', icon: <Gavel /> },
            { id: 'negotiator', label: 'Negotiation Facilitator', icon: <People /> },
          ]
            .map(track => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={track.id}>
                <Box
                  onClick={() => toggleRole(track.id)}
                  sx={{
                    border: '2px solid',
                    borderColor: selectedRolesArray.includes(track.id) ? 'primary.main' : '#e0e0e0',
                    bgcolor: selectedRolesArray.includes(track.id) ? (initialRoles.includes(track.id) ? 'rgba(0, 0, 0, 0.05)' : 'rgba(13, 110, 253, 0.05)') : 'transparent',
                    borderRadius: '8px',
                    p: 2,
                    textAlign: 'center',
                    cursor: initialRoles.includes(track.id) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    userSelect: 'none',
                    opacity: 1, // Always show all roles fully
                    '&:hover': {
                      borderColor: initialRoles.includes(track.id) ? 'primary.main' : 'primary.main',
                    }
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 1 }}>{track.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{track.label}</Typography>
                </Box>
              </Grid>
            ))}
        </Grid>
      </Paper>

    </Box>
  );
};

export default TracksAndServicesSection;
